import React from 'react';
import Card from '@/components/ui/Card';
import Button from '@/components/ui/Button';
import { Workspace } from '@/types';
import PlayerSearchSelect from './PlayerSearchSelect';
import WorkspaceCheckboxes from './WorkspaceCheckboxes';
import { ROLES, inputClasses, labelClasses } from './types';

interface CreateFormState {
  name: string;
  email: string;
  password: string;
  role: string;
  playerId: string;
  workspaceIds: string[];
}

const CreateUserForm: React.FC<{
  createForm: CreateFormState;
  setCreateForm: (form: CreateFormState) => void;
  createError: string;
  createLoading: boolean;
  allPlayers: { id: string; name: string }[];
  workspaces: Workspace[];
  onSubmit: (e: React.FormEvent) => void;
  onCancel: () => void;
}> = ({
  createForm,
  setCreateForm,
  createError,
  createLoading,
  allPlayers,
  workspaces,
  onSubmit,
  onCancel,
}) => (
  <Card>
    <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Create User</h2>
    <form onSubmit={onSubmit} className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label htmlFor="create-user-name" className={labelClasses}>Name</label>
          <input
            id="create-user-name"
            type="text"
            required
            value={createForm.name}
            onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })}
            className={inputClasses}
          />
        </div>
        <div>
          <label htmlFor="create-user-email" className={labelClasses}>Email</label>
          <input
            id="create-user-email"
            type="email"
            required
            value={createForm.email}
            onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })}
            className={inputClasses}
          />
        </div>
        <div>
          <label htmlFor="create-user-password" className={labelClasses}>Password</label>
          <input
            id="create-user-password"
            type="password"
            required
            minLength={6}
            value={createForm.password}
            onChange={(e) => setCreateForm({ ...createForm, password: e.target.value })}
            className={inputClasses}
          />
        </div>
        <div>
          <label htmlFor="create-user-role" className={labelClasses}>Role</label>
          <select
            id="create-user-role"
            value={createForm.role}
            onChange={(e) => setCreateForm({ ...createForm, role: e.target.value })}
            className={inputClasses}
          >
            {ROLES.map((r) => (
              <option key={r} value={r}>{r.charAt(0).toUpperCase() + r.slice(1)}</option>
            ))}
          </select>
        </div>
        {createForm.role === 'player' && (
          <div>
            <span className={labelClasses}>Link to Player</span>
            <PlayerSearchSelect
              players={allPlayers}
              value={createForm.playerId}
              onChange={(id) => setCreateForm({ ...createForm, playerId: id })}
              inputClasses={inputClasses}
            />
          </div>
        )}
        <div className="sm:col-span-2">
          <span className={labelClasses}>
            Workspaces
            {createForm.role === 'player' && !createForm.playerId && (
              <span className="text-red-500"> *</span>
            )}
          </span>
          {createForm.role === 'player' && createForm.playerId ? (
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Workspace is inherited from the linked player's squad.
            </p>
          ) : (
            <WorkspaceCheckboxes
              workspaces={workspaces}
              selected={createForm.workspaceIds}
              onChange={(ids) => setCreateForm({ ...createForm, workspaceIds: ids })}
            />
          )}
        </div>
      </div>
      {createError && (
        <div className="text-sm text-red-600 dark:text-red-400">{createError}</div>
      )}
      <div className="flex gap-3">
        <Button
          type="submit"
          loading={createLoading}
          loadingLabel="Creating user"
        >
          Create
        </Button>
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-md"
        >
          Cancel
        </button>
      </div>
    </form>
  </Card>
);

export default CreateUserForm;
